import React, { useRef, useState } from 'react';
import { Send, Smile, Reply, X, Image } from 'lucide-react';
import { Button } from '../ui/button';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import type { Message } from '../../types/chat';
import { isEmoji } from '@/lib/utils/emoji';
import { uploadMessageImage } from '@/lib/supabase/chat';

interface MessageInputProps {
  message: string;
  onMessageChange: (message: string) => void;
  onSend: () => void;
  onEmojiToggle?: () => void;
  replyingTo?: Message | null;
  onCancelReply?: () => void;
  onImageUpload?: (imageUrl: string) => void;
}

const MessageInput: React.FC<MessageInputProps> = ({
  message,
  onMessageChange,
  onSend,
  onEmojiToggle,
  replyingTo,
  onCancelReply,
  onImageUpload,
}) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { isDarkMode } = useTheme();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-resize textarea as content grows
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`; // Max 120px height
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (message?.trim()) {
        onSend();
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }
      }
    }
  };

  const handleSendClick = () => {
    if (message?.trim()) {
      onSend();
      
      // If sending just an emoji, provide visual feedback
      if (isEmoji(message) && message.trim().length <= 4) {
        const emojiElement = document.createElement('div');
        emojiElement.className = 'emoji-send-animation';
        emojiElement.textContent = message.trim();
        document.body.appendChild(emojiElement);
        
        setTimeout(() => {
          emojiElement.classList.add('animate-out');
          setTimeout(() => {
            document.body.removeChild(emojiElement);
          }, 500);
        }, 500);
      }
      
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const onEmojiClick = (emojiData: EmojiClickData) => {
    onMessageChange(message + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    
    // Check file size (15MB limit)
    if (file.size > 15 * 1024 * 1024) {
      alert('Image size must be less than 15MB');
      return;
    }
    
    try {
      setIsUploading(true);
      const imageUrl = await uploadMessageImage(file);
      
      if (onImageUpload) {
        onImageUpload(imageUrl);
      } else {
        // Fallback: insert image URL directly into message
        onMessageChange(`${message} [Image](${imageUrl})`);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="p-2 sm:p-4 border-t border-gray-200 dark:border-dark-300 w-full relative z-10 backdrop-blur-sm flex flex-col">
      {replyingTo && (
        <div className={cn(
          "flex items-center justify-between mb-2 px-2 py-1.5 rounded-lg",
          "dark:bg-dark-300/50",
          "light:bg-gray-100 light:border light:border-gray-200"
        )}>
          <div className="flex items-center space-x-2 text-sm">
            <Reply className={cn("h-4 w-4", "dark:text-gold-400", "light:text-gold-600")} />
            <span className={cn("dark:text-dark-800", "light:text-gray-500")}>Replying to</span>
            <span className={cn("truncate max-w-[200px]", "dark:text-white", "light:text-gray-900 light:font-medium")}>
              {replyingTo.content.length > 30 
                ? replyingTo.content.substring(0, 30) + '...' 
                : replyingTo.content}
            </span>
          </div>
          <button 
            onClick={onCancelReply}
            className={cn(
              "p-1 rounded-full",
              "dark:text-dark-800 dark:hover:text-white",
              "light:text-gray-500 light:hover:text-gray-900"
            )}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <div className="flex items-end gap-2 w-full bg-white/80 dark:bg-dark-300/80 rounded-xl p-2 backdrop-blur-sm">
        <div className="flex items-center space-x-2">
          <div className="relative">
            {/* Emoji Picker Button */}
            <Button
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              variant="ghost"
              size="icon"
              className={cn(
                "text-gray-500 hover:text-gold-600 h-10 w-10 p-1",
                "dark:text-dark-800 dark:hover:text-gold-400",
                "light:text-gray-500 light:hover:text-gold-600"
              )}
            >
              <Smile className="h-6 w-6" />
            </Button>
            {showEmojiPicker && (
              <div className="absolute bottom-10 left-0 z-50">
                <EmojiPicker
                  onEmojiClick={onEmojiClick}
                  autoFocusSearch={false}
                  theme={isDarkMode ? Theme.DARK : Theme.LIGHT}
                  searchPlaceHolder="Search emoji..."
                  width={300}
                  height={400}
                />
              </div>
            )}
          </div>
          
          {/* Image Upload Button */}
          <div className="relative">
            <Button
              onClick={handleImageClick}
              variant="ghost"
              size="icon"
              disabled={isUploading}
              className={cn(
                "text-gray-500 hover:text-gold-600 h-10 w-10 p-1 ml-0",
                "dark:text-dark-800 dark:hover:text-gold-400",
                "light:text-gray-500 light:hover:text-gold-600",
                isUploading && "opacity-50 cursor-not-allowed"
              )}
            >
              <Image className="h-6 w-6" />
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
            />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => {
              onMessageChange(e.target.value);
              adjustTextareaHeight();
            }}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            className={cn(
              "w-full rounded-lg px-3 py-1.5 max-h-[120px]",
              "text-sm",
              "focus:outline-none",
              "resize-none overflow-y-auto",
              "bg-white/50 text-gray-900 placeholder-gray-500",
              "dark:bg-dark-300/50 dark:text-white dark:placeholder-dark-800"
            )}
            rows={1}
          />
        </div>
        <Button
          onClick={handleSendClick}
          className={cn(
            "bg-gold-400 hover:bg-gold-500 text-dark-50 p-3 rounded-lg transition-colors flex-shrink-0",
            "shadow-md hover:shadow-lg transform hover:scale-105 active:scale-95",
            isUploading && "opacity-70 cursor-not-allowed"
          )}
          disabled={isUploading}
        >
          {isUploading ? (
            <div className="h-5 w-5 border-2 border-t-transparent border-dark-50 rounded-full animate-spin" />
          ) : (
            <Send className="h-6 w-6" />
          )}
        </Button>
      </div>
    </div>
  );
};

export default MessageInput;